import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useCurrentUserAccount from 'src/hooks/useCurrentUserAccount';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import { useTxErrorHandler } from 'src/hooks/velocityActions/transactions/useTxErrorHandler';
import { notify } from 'src/utils/notifications';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignVelocityActionWithFallback } from 'src/hooks/wallets/useAutoSignVelocityActionWithFallback';
import {
	createUpdateMarketMaxLeverageIxs,
	UIMarket,
} from '@velocity-exchange/common';
import { FeePayerError } from 'src/constants/errors/FeePayerError';

interface UseUpdateUserMarketMaxLeverageResult {
	updateUserMarketMaxLeverage: (
		toastId: string,
		marketIndex: number,
		leverage: number
	) => Promise<boolean>;
}

export const useUpdateUserMarketMaxLeverage =
	(): UseUpdateUserMarketMaxLeverageResult => {
		const velocityClient = useVelocityClient();
		const currentUserAccount = useCurrentUserAccount();

		const getTxParams = useGetTxParams();
		const handleTxError = useTxErrorHandler();

		const updateUserMarketMaxLeverage = useAutoSignVelocityActionWithFallback(
			useCallback(
				async (
					signerWallet: UiEnhancedIWalletV2,
					isAutoSigned: boolean,
					toastId: string,
					marketIndex: number,
					leverage: number
				): Promise<boolean> => {
					invariant(velocityClient, 'driftClient is required');
					invariant(currentUserAccount?.client, 'user client is required');

					const perpMarketAccount =
						velocityClient.getPerpMarketAccount(marketIndex);
					invariant(perpMarketAccount, 'perpMarketAccount is required');

					notify({
						id: toastId,
						type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
						message: isAutoSigned
							? 'Updating position max leverage...'
							: undefined,
						showUntilCancelled: true,
					});

					try {
						const ixs = await createUpdateMarketMaxLeverageIxs({
							velocityClient: velocityClient,
							user: currentUserAccount.client,
							perpMarketAccount,
							leverage,
							mainSignerOverride: signerWallet.publicKey,
						});

						if (ixs.length === 0) {
							notify({
								type: 'success',
								message: 'No changes needed.',
								id: toastId,
								updatePrevious: true,
							});
							return true;
						}

						const txParams = getTxParams();
						const txSig = await signerWallet.signAndSendTransaction(
							ixs,
							txParams
						);

						if (!txSig) {
							throw new Error('Transaction failed');
						}

						const marketSymbol =
							UIMarket.createPerpMarket(marketIndex).uiSymbols.marketSymbol;
						notify({
							type: 'success',
							message: `${marketSymbol} leverage updated.`,
							id: toastId,
							updatePrevious: true,
						});

						return true;
					} catch (error) {
						console.error('Error updating market max leverage', error);

						if (error instanceof FeePayerError) {
							throw error;
						}

						handleTxError(error, toastId);

						return false;
					}
				},
				[velocityClient, currentUserAccount, getTxParams, handleTxError]
			)
		);

		return { updateUserMarketMaxLeverage };
	};
